// OrderPosts.tsx
import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  List,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SearchFilter } from 'components/ui/SearchFilter';
import ownerAtom from 'data/atoms/ownerAtom';
import userAtom from 'data/atoms/userAtom';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rq, rqPost } from 'http/apiRoutes';
import { useGet, usePost } from 'http/useApi';
import {
  EnumCompanyType,
  enumCompanyTypeToStringMapping,
  EnumOrderPostStatus,
} from 'Interfaces/IGlobalTypeConfig';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LeftOrderTicketCard,
  toOrderPostCompany,
} from './Components/OrderComponents';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';

import {
  OrderPostCompanyViewModel,
  OrderPostProductViewModel,
  SingleOrderPostTicketViewModel,
} from './ViewModels/OrderPostViewModels';
// NYE imports
import {
  OwnerForCard,
  ProductUpdatePair,
  selectOrderingGroupsForCard,
  SetProductUpdatePayload,
} from './Reducers/OrderingProductReducer';

import {
  RootOrderReducer,
  initialRootOrderState,
} from './Reducers/RootOrderReducer';
import { AggregatedOrderProductCard } from './Components/AggregatedOrderProductCard';
import {
  clearDraft,
  DraftLine,
  getOrCreateDraft,
  loadDraft,
  OrderingDraft,
  saveDraft,
} from './storage/orderingDraft';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { CompaniesDrawer } from 'components/ui/Drawers/CompaniesDrawer';
import { SingleTicketCompanyView } from 'components/ui/SingleTicketCompanyView';
import { boxStyle, TextStyle } from 'data/FontStyle';
import { ICompanyViewModel } from 'Interfaces/ICompaniesConfig';
import {
  CreateOrderPostItem,
  CreateOrderPostsRequest,
  getCompanyRefs,
  indexTicketsById,
} from './Functions/orderFunctions';
import { normalizeFromBackend } from './Functions/normalizeFromBackend';

export const OrderPosts = () => {
  // *****************************************************************
  // HOOKS
  // *****************************************************************
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(RootOrderReducer, initialRootOrderState);
  const [isOrderPostsLoaded, setIsOrderPostsLoaded] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const { height } = useWindowsDimension();
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const [owner] = useAtom(ownerAtom);
  const [user] = useAtom(userAtom);
  // *****************************************************************
  // API
  // *****************************************************************
  const isEnabled = !isOrderPostsLoaded;
  const { data: orderPostData } = useGet(
    rq.getOrderPosts(
      EnumOrderPostStatus.WaitingToBeOrdered,
      state.orderPost.page,
      state.orderPost.pageSize,
      isEnabled
    ),
    
  );
  // get all vendors
  const { data: vendors } = useGet(
    rq.companies(enumCompanyTypeToStringMapping[EnumCompanyType.VENDOR], true)
  );
  const postOrder = usePost(
    rqPost.postOrder(), // ingen param nødvendig
 
  );

  // *****************************************************************
  // USE EFFECTS
  // *****************************************************************
  useEffect(() => {
    if (!vendors) return;
    const normalized = vendors.companies.map(toOrderPostCompany);
    dispatch({
      type: 'ORDER_POST/SET_VENDORS',
      payload: { vendors: normalized },
    });
  }, [vendors, dispatch]);

  useEffect(() => {
    if (!orderPostData) return;
    console.log('orderPostData:', orderPostData);

    const draft = loadDraft(); // kan være null
    const normalized = normalizeFromBackend(orderPostData);

    // 2a) Venstre side: oppdater fra draft (pris, URL, orderedQuantity)
    const leftPatched = { ...normalized };
    if (draft) {
      const selectedIds = new Set(Object.keys(draft.selectedProductIds || {}));

      leftPatched.orderPosts = leftPatched.orderPosts.map((t) => ({
        ...t,
        ticketProducts: t.ticketProducts.map((p) => {
          const draftLine = draft.lines[p.id];
          const urlKey = (
            draftLine?.vendorProductUrl ??
            p.vendorProductUrl ??
            ''
          ).trim();

          // valgt hvis eksplisitt valgt ELLER linja har bestilt antall i draft
          const isSelected =
            selectedIds.has(p.id) || (draftLine?.orderedQuantity ?? 0) > 0;

          // pris: linjepris > pris per URL > original
          const unitPriceFromUrl =
            urlKey && draft.unitPriceByUrl[urlKey] !== undefined
              ? draft.unitPriceByUrl[urlKey]!
              : undefined;

          const mergedUnitPrice =
            draftLine?.unitPrice ?? unitPriceFromUrl ?? p.unitPrice ?? 0;

          // URL: draft-linje > original
          const mergedUrl = urlKey;

          // antall: ta fra draft hvis valgt, og CAP mot behov
          const maxNeed = Math.max(
            0,
            (p.quantity ?? 0) - (p.alreadyOrdered ?? 0)
          );
          const orderedFromDraft = Math.min(
            maxNeed,
            Math.max(0, draftLine?.orderedQuantity ?? p.orderedQuantity ?? 0)
          );

          return {
            ...p,
            orderedQuantity: isSelected ? orderedFromDraft : 0,
            unitPrice: mergedUnitPrice,
            vendor: draft.selectedVendor ?? p.vendor ?? null,
            vendorProductUrl: mergedUrl,
          };
        }),
      }));
    }

    console.log('OrderPosts - leftPatched:', leftPatched);

    // 2b) Venstre side inn i store
    dispatch({ type: 'ORDER_POST/SUCCESS', payload: leftPatched });

    if (draft?.selectedVendor !== undefined) {
      console.log(
        'UIX Setting selected vendor from draft:',
        draft.selectedVendor
      );
      dispatch({
        type: 'ORDER_POST/SET_SELECTED_VENDOR',
        payload: { vendor: draft.selectedVendor ?? null },
      });
    }
    if (draft?.freightCost !== undefined) {
      console.log('UIX Setting freight cost from draft:', draft.freightCost);
      dispatch({
        type: 'ORDER_POST/SET_FREIGHT_COST',
        payload: { freightCost: draft.freightCost ?? 0 },
      });
    }
    if (draft?.vendorOrderNumber !== undefined) {
      console.log(
        'UIX Setting vendor order number from draft:',
        draft.vendorOrderNumber
      );
      dispatch({
        type: 'ORDER_POST/SET_VENDOR_ORDER_NUMBER',
        payload: { vendorOrderNumber: draft.vendorOrderNumber ?? '' },
      });
    }

    // 2c) Høyre side: bygg fra det som faktisk er "valgt" etter patch
    const selectedSet = new Set(Object.keys(draft?.selectedProductIds ?? {}));
    const rightTickets = !draft
      ? []
      : leftPatched.orderPosts
          .map((t) => ({
            ...t,
            ticketProducts: t.ticketProducts.filter((p) => {
              const dl = draft.lines[p.id];
              // valgt hvis: eksplisitt valgt ELLER linje har bestilt antall
              const isSelected =
                selectedSet.has(p.id) || (dl?.orderedQuantity ?? 0) > 0;
              return isSelected;
            }),
          }))
          .filter((t) => (t.ticketProducts?.length ?? 0) > 0);

    dispatch({
      type: 'ORDERING/INIT_FROM_TICKETS',
      payload: { tickets: rightTickets },
    });

    setIsOrderPostsLoaded(true);
  }, [orderPostData]);

  useEffect(() => {
    setIsOrderPostsLoaded(false);
  }, [state.orderPost.page, state.orderPost.pageSize]);

  // *****************************************************************
  // HELPER FUNCTIONS
  // *****************************************************************
  function findLineByUrl(draft: OrderingDraft, url: string) {
    return (
      Object.values(draft.lines ?? {}).find(
        (l: DraftLine) => l.vendorProductUrl === url
      )?.unitPrice ?? null
    );
  }
  // hent grupper for kortvisning
  const groups =
    selectOrderingGroupsForCard({ ordering: state.ordering }) ?? [];

  // Sterkt typet retur
  type Totals = { productsTotal: number; freight: number; grandTotal: number };

  const { productsTotal, freight, grandTotal } = useMemo<Totals>(() => {
    const productsTotal = groups.reduce((sum, g) => {
      const ordered = Number(g?.orderedQuantity) || 0;
      const unit = Number(g?.unitPrice) || 0;
      return sum + ordered * unit;
    }, 0);

    // NB: feltet heter "freightCost" hos deg
    const freight = Number(state?.orderPost?.freightCost) || 0;

    return {
      productsTotal,
      freight,
      grandTotal: productsTotal + freight,
    };
  }, [groups, state?.orderPost?.freightCost]);

  function addLineWithFullOrder(
    ticket: SingleOrderPostTicketViewModel,
    product: OrderPostProductViewModel
  ) {
    // full qty = behov = quantity - alreadyOrdered (aldri negativ)
    const fullOrdered = Math.max(
      0,
      (product.quantity ?? 0) - (product.alreadyOrdered ?? 0)
    );

    // hent ev. pris via URL fra draft om tilgjengelig
    const d0 = getOrCreateDraft();
    const urlKey = (product.vendorProductUrl || '').trim();
    const priceFromUrl =
      urlKey && d0.unitPriceByUrl[urlKey] !== undefined
        ? d0.unitPriceByUrl[urlKey]!
        : undefined;

    const unitPrice = product.unitPrice ?? priceFromUrl ?? 0;

    // ---- HØYRE SIDE (grupper) ----
    const lineForRight: OrderPostProductViewModel = {
      ...product,
      orderedQuantity: fullOrdered,
      unitPrice,
    };

    dispatch({
      type: 'ORDERING/ADD_LINE_FROM_TICKET',
      payload: { ticket, line: lineForRight },
    });

    // ---- VENSTRE SIDE (patch direkte på ticketProduct) ----
    // Bruk den generelle patcheren du har lagt inn:
    dispatch({
      type: 'ORDER_POST/UPDATE_ORDER_POST_FIELDS',
      payload: {
        ticketProductId: product.id,
        orderedQuantity: fullOrdered,
        unitPrice,
        vendorProductUrl: urlKey, // behold/sett URL også
      },
    });

    // ---- DRAFT ----
    const d = getOrCreateDraft();
    d.selectedProductIds[product.id] = true;
    d.lines[product.id] = {
      ...(d.lines[product.id] ?? { productId: product.id }),
      productId: product.id,
      orderedQuantity: fullOrdered,
      unitPrice,
      vendorProductUrl: urlKey,
    };
    // speil ev. pris på url-nivå (nyttig senere)
    if (urlKey) d.unitPriceByUrl[urlKey] = unitPrice;

    saveDraft(d);
  }

  // *****************************************************************
  // HANDLERS
  // *****************************************************************
  const handleChangeFreightCost = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value) || 0;
    const capped = Math.max(0, v);
    dispatch({
      type: 'ORDER_POST/SET_FREIGHT_COST',
      payload: { freightCost: capped },
    });
    const d = getOrCreateDraft();
    d.freightCost = capped;
    saveDraft(d);
  };
  const handleChangeOrderNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value || '';
    dispatch({
      type: 'ORDER_POST/SET_VENDOR_ORDER_NUMBER',
      payload: { vendorOrderNumber: v },
    });
    const d = getOrCreateDraft();
    d.vendorOrderNumber = v;
    saveDraft(d);
  };
  const handleChangeVendor = useCallback(
    (event: React.MouseEvent<HTMLElement>, company: ICompanyViewModel) => {
      const vendor = toOrderPostCompany(company as ICompanyViewModel);
      dispatch({ type: 'ORDER_POST/SET_SELECTED_VENDOR', payload: { vendor } });
      const d = getOrCreateDraft();
      d.selectedVendor = vendor ?? null;
      saveDraft(d);
    },
    [dispatch]
  );
  const setSelectedVendor = (vendor: OrderPostCompanyViewModel | null) => {
    dispatch({ type: 'ORDER_POST/SET_SELECTED_VENDOR', payload: { vendor } });
    const d = getOrCreateDraft();
    d.selectedVendor = vendor ?? null;
    saveDraft(d);
  };
  const handleClearAllOrders = () => {
    // 1) Høyre: tøm alle grupper
    dispatch({ type: 'ORDERING/CLEAR_ALL' });

    // 2) Venstre: nullstill alt bestilt
    dispatch({ type: 'ORDER_POST/CLEAR_ALL_ORDERED' });

    // 3) remove selected Vendor also
    dispatch({
      type: 'ORDER_POST/SET_SELECTED_VENDOR',
      payload: { vendor: null },
    });

    // remove freight and order number
    dispatch({
      type: 'ORDER_POST/SET_FREIGHT_COST',
      payload: { freightCost: 0 },
    });
    dispatch({
      type: 'ORDER_POST/SET_ORDER_NUMBER',
      payload: { vendorOrderNumber: '' },
    });

    // 4) Draft: tøm alt
    clearDraft();
  };
  const handleChangeOwnerOrdered = (
    productId: string,
    next: number,
    owners: OwnerForCard[],
    ownerId: string
  ) => {
    dispatch({
      type: 'ORDERING/SET_OWNER_ORDERED',
      payload: { productId, orderedQuantity: next, ownerId },
    });
    dispatch({
      type: 'ORDER_POST/SET_OWNER_ORDERED',
      payload: { productId, orderedQuantity: next, ownerId },
    });

    const d = getOrCreateDraft();
    d.lines[productId] = {
      ...(d.lines[productId] ?? { productId }),
      productId,
      orderedQuantity: next,
      vendorProductUrl: d.lines[productId]?.vendorProductUrl,
    };
    d.selectedProductIds[productId] = true; // 👈 behold valgt selv om next = 0
    saveDraft(d);
  };
  // HANDLERS (nå dispatch direkte til lokalt reducer-state):
  const handleAddProductToOrderList = (product: OrderPostProductViewModel) => {
    const ticket = state.orderPost.orderPosts.find((t) =>
      t.ticketProducts?.some((p) => p.id === product.id)
    );
    if (!ticket) return;

    const selectedVendor: OrderPostCompanyViewModel | null =
      state.orderPost.selectedVendor ?? null;
    const prodVendor: OrderPostCompanyViewModel | null = product.vendor ?? null;

    // 1) Ingen valgt vendor: sett til produktets vendor (om finnes) og legg til
    if (!selectedVendor) {
      if (prodVendor) {
        setSelectedVendor(prodVendor); // oppdater state + draft
        const d = getOrCreateDraft();
        d.selectedVendor = prodVendor;
        saveDraft(d);
      }
      addLineWithFullOrder(ticket, product);
      return;
    }

    // 2) Vendor match: legg til (full qty første gang, eller behold draft-verdi)
    if (prodVendor && prodVendor.id === selectedVendor.id) {
      const d = getOrCreateDraft();
      const hasExisting = !!d.lines[product.id];

      // forsøk å få pris fra URL hvis tilgjengelig
      const urlKey = (product.vendorProductUrl || '').trim();
      const priceFromUrl =
        urlKey && d.unitPriceByUrl[urlKey] !== undefined
          ? d.unitPriceByUrl[urlKey]!
          : undefined;

      if (hasExisting) {
        // finnes i draft: respekter tidligere orderedQuantity og pris
        d.selectedProductIds[product.id] = true;
        if (!d.lines[product.id]) {
          d.lines[product.id] = {
            productId: product.id,
            orderedQuantity: product.orderedQuantity ?? 0,
            vendorProductUrl: product.vendorProductUrl,
            unitPrice: product.unitPrice ?? priceFromUrl ?? 0,
          };
        }

        const lineForRight: OrderPostProductViewModel = {
          ...product,
          orderedQuantity:
            d.lines[product.id].orderedQuantity ?? product.orderedQuantity ?? 0,
          unitPrice:
            d.lines[product.id].unitPrice ??
            product.unitPrice ??
            priceFromUrl ??
            0,
        };

        dispatch({
          type: 'ORDERING/ADD_LINE_FROM_TICKET',
          payload: { ticket, line: lineForRight },
        });

        // patch venstre side i samme slengen
        dispatch({
          type: 'ORDER_POST/UPDATE_ORDER_POST_FIELDS',
          payload: {
            ticketProductId: product.id,
            orderedQuantity: lineForRight.orderedQuantity ?? 0,
            unitPrice: lineForRight.unitPrice ?? 0,
            vendorProductUrl: (product.vendorProductUrl || '').trim(),
          },
        });

        saveDraft(d);
      } else {
        // første gang: full qty begge sider + draft
        // justér pris via URL hvis vi har den
        const patched: OrderPostProductViewModel = {
          ...product,
          unitPrice: product.unitPrice ?? priceFromUrl ?? 0,
        };
        addLineWithFullOrder(ticket, patched);
      }
      return;
    }

    // 3) Vendor mismatch: spør om å bytte, evt. legg til med valgt vendor
    const productVendorName = prodVendor?.name ?? '(ukjent)';
    const selectedVendorName = selectedVendor?.name ?? '(ukjent)';

    const choice = window.confirm(
      `Valgt leverandør er: ${selectedVendorName}.\n` +
        `Produktets leverandør er: ${productVendorName}.\n\n` +
        `OK = Bytt Leverandør på produkt.\n` +
        `Avbryt = Ikke legg til dette produktet.`
    );

    if (choice) {
      const patched: OrderPostProductViewModel = {
        ...product,
        vendor: selectedVendor,
      };
      const d = getOrCreateDraft();
      d.selectedVendor = selectedVendor;
      saveDraft(d);

      addLineWithFullOrder(ticket, patched);
    }
  };
  // endre felles kostpris
  const handleUpdateProductFields = (
    productId: string,
    nextUnitPriceOrUndefined: number | undefined,
    vendorProductUrl: string | undefined,
    owners: OwnerForCard[],
    quantitiesByOwnerId?: Record<string, number | undefined>
  ) => {
    const trimmedUrl = (vendorProductUrl || '').trim();

    const pairs: ProductUpdatePair[] = owners.map((o) => ({
      productId: o.productId,
      ownerId: o.owner?.id ?? '', // '' = ingen eier
      ticketId: (o as any).ticketId ?? null,
      quantity: quantitiesByOwnerId
        ? quantitiesByOwnerId[o.owner?.id ?? '']
        : undefined,
    }));

    const payload: SetProductUpdatePayload = {
      unitPrice: nextUnitPriceOrUndefined,
      vendorProductUrl: trimmedUrl || undefined, // tom => undefined
      pairs,
    };

    // Høyre (grupper)
    dispatch({ type: 'ORDERING/UPDATE_PRODUCT_FIELDS', payload });

    // Venstre (tickets)
    dispatch({ type: 'ORDER_POST/UPDATE_PRODUCT_FIELDS', payload });

    // Draft
    const d = getOrCreateDraft();

    for (const p of pairs) {
      if (payload.unitPrice !== undefined) {
        d.lines[p.productId] = {
          ...(d.lines[p.productId] ?? { productId: p.productId }),
          unitPrice: payload.unitPrice,
        };
      }
      if (p.quantity !== undefined) {
        d.lines[p.productId] = {
          ...(d.lines[p.productId] ?? { productId: p.productId }),
          orderedQuantity: p.quantity,
        };
        d.selectedProductIds[p.productId] = true;
      }
    }

    // speil pris til alle venstre-linjer med samme URL
    if (payload.unitPrice !== undefined && trimmedUrl) {
      for (const t of state.orderPost.orderPosts ?? []) {
        for (const tp of t.ticketProducts ?? []) {
          if ((tp.vendorProductUrl || '').trim() === trimmedUrl) {
            d.lines[tp.id] = {
              ...(d.lines[tp.id] ?? { productId: tp.id }),
              unitPrice: payload.unitPrice,
            };
          }
        }
      }
    }

    saveDraft(d);
  };
  const handleChangeVendorUrl = (oldUrl: string, nextUrl: string) => {
    dispatch({
      type: 'ORDERING/SET_GROUP_VENDOR_URL',
      payload: { oldUrl, nextUrl },
    });
    dispatch({
      type: 'ORDER_POST/UPDATE_VENDOR_URL',
      payload: { oldUrl, nextUrl },
    });

    const d = getOrCreateDraft();
    const moved = d.unitPriceByUrl[nextUrl];
    if (moved !== undefined) {
      dispatch({
        type: 'ORDER_POST/SET_GROUP_UNIT_PRICE',
        payload: { vendorProductUrl: nextUrl, unitPrice: moved },
      });
    }
    const grp = state.ordering.groups?.[oldUrl];
    if (grp) {
      for (const line of grp.lines) {
        // behold valgt og flytt URL i draft
        d.selectedProductIds[line.id] = true;
        d.lines[line.id] = {
          ...(d.lines[line.id] ?? { productId: line.id }),
          productId: line.id,
          orderedQuantity:
            d.lines[line.id]?.orderedQuantity ?? line.orderedQuantity ?? 0,
          vendorProductUrl: nextUrl,
          unitPrice: d.lines[line.id]?.unitPrice ?? line.unitPrice ?? 0,
        };
      }
    }
    // flytt ev. unitPrice
    if (d.unitPriceByUrl[oldUrl] !== undefined) {
      d.unitPriceByUrl[nextUrl] = d.unitPriceByUrl[oldUrl];
      delete d.unitPriceByUrl[oldUrl];
    }
    saveDraft(d);
  };
  // slette gruppe
  const handleRemoveGroup = (urlKey: string) => {
    dispatch({
      type: 'ORDERING/REMOVE_GROUP',
      payload: { vendorProductUrl: urlKey },
    });
    dispatch({
      type: 'ORDER_POST/CLEAR_VENDOR_URL_ORDERED',
      payload: { vendorProductUrl: urlKey },
    });

    const d = getOrCreateDraft();
    // fjern “selected” for alle linjer i gruppa
    const grp = state.ordering.groups?.[urlKey];
    if (grp) {
      for (const line of grp.lines) {
        delete d.selectedProductIds[line.id]; // 👈 viktig
        // du kan gjerne sette orderedQuantity=0 også (du gjør det allerede)
        d.lines[line.id] = {
          ...(d.lines[line.id] ?? { productId: line.id }),
          productId: line.id,
          orderedQuantity: 0,
          vendorProductUrl: d.lines[line.id]?.vendorProductUrl ?? urlKey,
        };
      }
    }
    saveDraft(d);
  };
  // sette bestilling for én eier
  const handleRemoveOwner = (productId: string, ownerId: string) => {
    console.log('UIX handleRemoveOwner', { productId, ownerId });
    dispatch({
      type: 'ORDERING/REMOVE_OWNER_FROM_LINE',
      payload: { productId, ownerId },
    });
    dispatch({
      type: 'ORDER_POST/REMOVE_OWNER_FROM_LINE',
      payload: { productId, ownerId },
    });
    //fjern “selected” for linjen til eieren
    const draft_copy = getOrCreateDraft();
    console.log('UIX draft before removing owner:', draft_copy);
    delete draft_copy.selectedProductIds[productId]; // 👈 viktig
    delete draft_copy.lines[productId];
    saveDraft(draft_copy);
  };

  // *****************************************************************
  // PAGINATION
  // *****************************************************************
  const goPrev = () => {
    if (state.orderPost.page > 1)
      dispatch({
        type: 'ORDER_POST/SET_PAGE',
        payload: state.orderPost.page - 1,
      });
  };
  const goNext = () => {
    if (state.orderPost.page < totalPages)
      dispatch({
        type: 'ORDER_POST/SET_PAGE',
        payload: state.orderPost.page + 1,
      });
  };
  const changePageSize = (val: number) => {
    dispatch({ type: 'ORDER_POST/SET_PAGE_SIZE', payload: val });
  };

  // *****************************************************************
  // ON SUBMIT
  // *****************************************************************
  // legg gjerne øverst i filen (eller i egen util)
  function wipeOrderingLocalState() {
    try {
      // dagens draft
      clearDraft();

      // ev. gamle/legacy nøkler (hoppes over hvis de ikke finnes)
      localStorage.removeItem('orderingDraft');
      localStorage.removeItem('ordering.unitPriceByUrl');
      localStorage.removeItem('ordering.selectedProductIds');
      localStorage.removeItem('ordering.vendorId');
      localStorage.removeItem('ordering.freightCost');
      localStorage.removeItem('ordering.vendorOrderNumber');
    } catch (e) {
      console.warn('wipeOrderingLocalState failed:', e);
    }
  }
  const onSubmit = async () => {
    try {
      // 1) Grupper for høyresiden
      const groups = state.ordering.groups || {};
      const groupValues = Object.values(groups);

      // 2) Oppslag venstreside (ticketId -> ticket)
      const ticketIndex = indexTicketsById(state.orderPost.orderPosts || []);

      // 3) Headerdata
      const selectedVendorId = state.orderPost.selectedVendor?.id ?? null;
      const freightCost = state.orderPost.freightCost ?? 0;
      const vendorOrderNumber =
        state.orderPost.vendorOrderNumber.trim().length > 0
          ? state.orderPost.vendorOrderNumber.trim()
          : '';

      // 4) Items
      const items: CreateOrderPostItem[] = [];
      for (const g of groupValues) {
        const groupUnitPrice = Number(g.unitPrice) || 0;
        for (const ln of g.lines) {
          const ordered = Number(ln.orderedQuantity) || 0;
          if (ordered <= 0) continue;

          const { companyId, companyContactId } = getCompanyRefs(
            ticketIndex,
            ln.ticketId
          );

          const vendorId =
            selectedVendorId ?? ln.vendor?.id ?? g.vendor?.id ?? null;

          let status = EnumOrderPostStatus.WaitingToBeOrdered;
          if (ordered > 0 && ordered < (ln.quantity || 0))
            status = EnumOrderPostStatus.PartialOrdered;
          if (ordered >= (ln.quantity || 0))
            status = EnumOrderPostStatus.Ordered;

          items.push({
            ticketProductId: ln.id,
            orderPostId: ln.orderPostId,
            ticketId: ln.ticketId,
            companyId,
            companyContactId,
            vendorId,
            unitPrice: groupUnitPrice,
            retailPrice: Number(ln.retailPrice) || 0,
            quantity: Number(ln.quantity) || 0,
            orderedQuantity: ordered,
            vendorProductUrl: ln.vendorProductUrl || g.vendorProductUrl,
            deliveryDate: null,
            status,
          });
        }
      }

      // 5) Payload
      if (!user) throw new Error('User not defined');
      const payload: CreateOrderPostsRequest = {
        freightCost: freightCost > 0 ? freightCost : null,
        vendorOrderNumber: vendorOrderNumber ?? null,
        vendorId: selectedVendorId,
        responsibleId: user.id ?? null,
        items,
      };

      // 6) Guard – ingen linjer
      if (items.length === 0) {
        console.warn('Ingen linjer å bestille.');
        return;
      }

      // 7) Kall backend (bruk mutateAsync for enkel flyt)
      console.error('onSubmit payload:', payload);

      await postOrder.mutateAsync(payload);

      // 8) Success: rydde ALT lokalt + nullstill UI
      wipeOrderingLocalState(); // <- sletter draft + ev. legacy-keys
      handleClearAllOrders(); // <- nullstill høyre/venstre reducers
      setIsOrderPostsLoaded(false); // <- trigger refetch i din useEffect
    } catch (err) {
      console.error('onSubmit error:', err);
      // vis snackbar/alert om ønskelig
    }
  };
  // *****************************************************************
  // DERIVED STATE (brukMemo der det er hensiktsmessig)
  // *****************************************************************

  // under groups-variabelen
  const hasZeroUnitPrice = (() => {
    const d = loadDraft();
    if (!d) return false;

    const lines = Object.values(d.lines).filter(
      (l) => !!d.selectedProductIds?.[l.productId]
    );

    return lines.some((l) => (Number(l.unitPrice) || 0) === 0);
  })();

  // ingen valgt?
  const noSelection = groups.length === 0;

  // Om du kun vil sjekke kostpris (unit) — bytt til kun hasZeroUnit
  const hasZeroPrice = hasZeroUnitPrice;
  const noOrderNumber = state.orderPost.vendorOrderNumber.length === 0;
  const noFreight = (state.orderPost.freightCost ?? 0) === 0;

  // *****************************************************************
  // RENDER
  // *****************************************************************
  return (
    <Grid
      container
      sx={{
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      {/* LEFT SIDE */}
      <Grid item xs={6} sx={{ pr: 0.5, m: 0 }}>
        <SearchFilter
          Icon={CreditCardIcon}
          Header={t('Orders.OrderPosts.unmanagedProducts')}
        />
        <Grid
          item
          xs={12}
          sx={{
            p: 0,
            m: 0,
            backgroundColor: 'primary.main',
          }}
        >
          <List
            sx={{
              p: 2,
              height: Math.ceil(height - 252),
              backgroundColor: 'primary.back',
              overflow: 'auto',
            }}
          >
            {(() => {
              const seen = new Set<string>();
              const unique = (state.orderPost.orderPosts ?? []).filter((op) => {
                if (seen.has(op.ticketId)) return false;
                seen.add(op.ticketId);
                return true;
              });

              return unique.map((orderPost) => (
                <LeftOrderTicketCard
                  key={orderPost.ticketId} // stabil og unik
                  item={orderPost}
                  onPickProduct={handleAddProductToOrderList}
                  getOrderedCount={() => 0}
                />
              ));
            })()}
          </List>
        </Grid>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 16 }}>
            {t('order.orderPost.page')} {state.orderPost.page} / {totalPages}
            {'  '}({state.orderPost.totalCount} {t('order.orderPost.items')})
          </Typography>
          <div>
            <Button
              size='small'
              onClick={goPrev}
              disabled={state.orderPost.page <= 1}
              sx={{ mr: 1 }}
            >
              {t('order.orderPost.previous')}
            </Button>
            <Button
              size='small'
              onClick={goNext}
              disabled={state.orderPost.page >= totalPages}
              sx={{ mr: 2 }}
            >
              {t('order.orderPost.next')}
            </Button>
            <Select
              size='small'
              value={state.orderPost.pageSize}
              onChange={(e) => changePageSize(Number(e.target.value))}
              sx={{ height: 32, minWidth: 90 }}
            >
              {[10, 25, 50, 100].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </div>
        </Paper>
      </Grid>

      {/*   RIGHT SIDE    */}
      <Grid item xs={6} sx={{ pl: 0.5, m: 0 }}>
        {/*   HEADER    */}
        <Paper
          variant='elevation3'
          sx={{ borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }}
        >
          <Grid container display={'flex'} flexDirection={'row'}>
            <Grid item xs={1} sx={{ p: 0, m: 0 }}>
              <CreditCardIcon sx={{ fontSize: 32, m: 1.5 }} />
            </Grid>
            <Grid item xs={2} sx={{ pl: 1, m: 0 }}>
              <Typography
                sx={{
                  m: 2,
                  ml: -1,
                  pt: 0.5,
                  flexGrow: 1,
                  textTransform: 'uppercase',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 'bold',
                  fontSize: 14,
                  letterSpacing: 2,
                }}
              >
                {t('Orders.OrderPosts.orders')}
              </Typography>
            </Grid>
            <Grid item xs={8} sx={{ p: 0, m: 0, mt: 0.7, pr: 1 }}></Grid>
            {/* CLEAR-knapp helt til høyre */}
            <Grid
              item
              xs={1}
              sx={{
                p: 0,
                m: 0,
                mt: 0.7,
                pr: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Tooltip title={t('Orders.OrderPosts.clearAll') || 'Tøm alle'}>
                <IconButton
                  aria-label='clear-all-orders'
                  onClick={handleClearAllOrders}
                  size='small'
                  sx={{
                    bgcolor: 'action.hover',
                    '&:hover': {
                      bgcolor: 'secondary.light',
                      color: 'secondary.contrastText',
                    },
                    borderRadius: 2,
                    width: 62,
                    height: 62,
                  }}
                >
                  <ClearAllIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
        {/*    VENDOR INFO    */}
        <Grid
          container
          sx={{
            p: 1,
            m: 0,
            display: 'flex',
            flexDirection: 'row',
            height: 128,
          }}
        >
          <Grid
            item
            xs={6}
            sx={{
              p: 0,
              m: 0,
              backgroundColor: 'primary.dark',
              mt: 1,
            }}
          >
            {/* Vendor selection */}
            <Box sx={{ position: 'relative', mb: 0 }}>
              {state.orderPost.selectedVendor &&
              state.orderPost.selectedVendor.id ? (
                <Stack direction='column' sx={{ width: '100%' }}>
                  <SingleTicketCompanyView
                    company={state.orderPost.selectedVendor}
                  />
                  <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                    <CompaniesDrawer
                      buttonContext={t('ticketProductDrawer.selectVendor')}
                      buttonIcon={<ChangeCircleIcon sx={{ fontSize: 26 }} />}
                      handleChange={handleChangeVendor}
                      isHideCompanyView
                      isHideCompanyNavigation
                      companyType={EnumCompanyType.VENDOR}
                    />
                  </Box>
                </Stack>
              ) : (
                <Box
                  sx={
                    [
                      boxStyle.base,
                      { height: 60, width: '100%', position: 'relative' },
                    ] as any
                  }
                >
                  <Button
                    sx={{
                      mx: 'auto',
                      width: '100%',
                      height: '100%',
                      borderRadius: 3,
                    }}
                    variant='contained'
                    onClick={() => {
                      /* handleAssignMe(); */
                    }}
                  >
                    <Typography sx={[TextStyle.text, {}]}>
                      {t('order.orderPost.selectVendor')}
                    </Typography>
                  </Button>
                  <Box sx={{ position: 'absolute', top: 0, right: 3 }}>
                    <CompaniesDrawer
                      buttonContext={t('order.orderPost.selectVendor')}
                      buttonIcon={<ChangeCircleIcon sx={{ fontSize: 26 }} />}
                      handleChange={handleChangeVendor}
                      isHideCompanyView
                      isHideCompanyNavigation
                      companyType={EnumCompanyType.VENDOR}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
          {/* Order summary */}
          <Grid
            item
            xs={3}
            sx={{
              pr: 2,
              m: 0,
              backgroundColor: 'primary.dark',
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                height: 38,
                alignItems: 'right',
                textAlign: 'right',
              }}
            >
              {t('order.orderPost.freightCost')}:
            </Typography>
            <Typography
              sx={{
                fontSize: 20,
                height: 38,
                alignItems: 'right',
                textAlign: 'right',
              }}
            >
              {t('order.orderPost.totalCost')}:
            </Typography>
            <Typography
              sx={{
                fontSize: 20,
                height: 38,
                alignItems: 'right',
                textAlign: 'right',
              }}
            >
              {t('order.orderPost.vendorOrderNumber')}:
            </Typography>
          </Grid>
          {/* Order totalvalue */}
          <Grid
            item
            xs={3}
            sx={{
              p: 1,
              m: 0,
              backgroundColor: 'primary.dark',
            }}
          >
            <TextField
              size='small'
              type='number'
              value={state.orderPost.freightCost ?? 0}
              onFocus={(e) => (e.target as HTMLInputElement).select()}
              onChange={(e) =>
                handleChangeFreightCost(
                  e as React.ChangeEvent<HTMLInputElement>
                )
              }
              sx={{
                width: '100%',
                mt: -1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  height: 38,
                },
                // høyrejuster tallene
                '& input': {
                  textAlign: 'right',
                  paddingRight: 0, // litt strammere mot valutaen
                },
                // fjern spinner
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position='end'
                    sx={{ color: 'text.secondary', mr: 0.5, fontSize: 12 }}
                  >
                    {t('bluu.currency')}
                  </InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  step: '0.01',
                  inputMode: 'decimal',
                },
              }}
            />

            <Typography
              sx={{
                width: '100%',
                fontSize: 20,
                height: 38,
                alignItems: 'right',
                textAlign: 'right',
              }}
            >
              {grandTotal.toFixed(2)} {t('bluu.currency')}
            </Typography>
            {/*   VENDOR ORDER NUMBER    */}
            <TextField
              size='small'
              type='text'
              value={state.orderPost.vendorOrderNumber ?? ''}
              onFocus={(e) => (e.target as HTMLInputElement).select()}
              onChange={(e) =>
                handleChangeOrderNumber(
                  e as React.ChangeEvent<HTMLInputElement>
                )
              }
              sx={{
                width: '100%',
                mt: -1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  height: 45,
                },
                // høyrejuster tallene
                '& input': {
                  textAlign: 'right',
                  paddingRight: 0, // litt strammere mot valutaen
                },
                // fjern spinner
                '& input[type=number]': { MozAppearance: 'textfield' },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                  {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
              }}
            />
          </Grid>
        </Grid>
        {/*   ORDER POSTS    */}
        <Grid
          item
          xs={12}
          sx={{
            p: 1,
            m: 0,
            backgroundColor: 'primary.main',
          }}
        >
          <List
            sx={{
              p: 1,
              height: Math.ceil(height - 598),
              backgroundColor: 'primary.back',
              overflow: 'auto',
            }}
          >
            {groups.length === 0 && (
              <Typography sx={{ p: 2 }}>
                {t('Orders.OrderPosts.noProductsSelected')}
              </Typography>
            )}
            <Stack>
              {groups.map((g) => (
                <AggregatedOrderProductCard
                  key={g.id}
                  t={t}
                  group={{
                    id: g.id,
                    vendorProductUrl: g.vendorProductUrl,
                    name: g.name,
                    imageUrl: g.imageUrl,
                    vendor: g.vendor,
                    quantity: g.quantity,
                    orderedQuantity: g.orderedQuantity,
                    alreadyOrdered: g.alreadyOrdered,
                    unitPrice: g.unitPrice,
                    retailPrice: g.retailPrice,
                    owners: g.owners, // <- viktig: OwnerForCard[]
                  }}
                  handleUpdateProductFields={handleUpdateProductFields}
                  onRemoveGroup={handleRemoveGroup}
                  onRemoveOwner={handleRemoveOwner}
                  onChangeVendorUrl={handleChangeVendorUrl}
                  onChangeOwnerOrdered={handleChangeOwnerOrdered}
                />
              ))}
            </Stack>
          </List>
        </Grid>

        {/*   FOOTER    */}
        <Paper
          variant='elevation3'
          sx={{
            p: 1,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            backgroundColor: noSelection
              ? 'primary.main'
              : hasZeroPrice
              ? 'warning.light'
              : noOrderNumber
              ? 'error.light'
              : 'primary.main',
          }}
        >
          {/* Valgfritt: advarselstekst over knappene */}

          {hasZeroPrice ? (
            <Alert severity='warning' sx={{ mb: 1 }}>
              {t('order.orderPost.warningZeroPrice')}
            </Alert>
          ) : noSelection ? (
            <Alert severity='info' sx={{ mb: 1 }}>
              {t('order.orderPost.noProductsSelected')}
            </Alert>
          ) : (
            <Alert severity='success' sx={{ mb: 1 }}>
              {t('order.orderPost.infoAllPricesSet')}
            </Alert>
          )}
          {noOrderNumber && !noSelection ? (
            <Alert severity='warning' sx={{ mb: 1 }}>
              {t('order.orderPost.noOrderNumber')}
            </Alert>
          ) : noSelection ? (
            <Alert severity='info' sx={{ mb: 1 }}>
              {t('order.orderPost.noProductsSelected')}
            </Alert>
          ) : (
            <Alert severity='success' sx={{ mb: 1 }}>
              {t('order.orderPost.orderNumberOk')}
            </Alert>
          )}
          {noFreight && !noSelection ? (
            <Alert severity='warning' sx={{ mb: 1 }}>
              {t('order.orderPost.noFreightCost')}
            </Alert>
          ) : noSelection ? (
            <Alert severity='info' sx={{ mb: 1 }}>
              {t('order.orderPost.noProductsSelected')}
            </Alert>
          ) : (
            <Alert severity='success' sx={{ mb: 1 }}>
              {t('order.orderPost.freightCostOk')}
            </Alert>
          )}

          <Grid
            item
            xs={12}
            sx={{
              p: 0,
              m: 0,
              display: 'flex',
              width: '100%',
              flexDirection: 'row-reverse',
            }}
          >
            {/* Primær: Lagre */}
            {!pendingConfirm && (
              <Button
                variant='contained'
                sx={{ mr: 1, width: '40%' }}
                onClick={() => {
                  setPendingConfirm(true);
                }}
                disabled={pendingConfirm || noSelection} // mens bekreftelse er aktiv, tving brukeren til å trykke "Bekreft lagring"
              >
                {t('Orders.OrderPosts.saveOrder')}
              </Button>
            )}
            {/* Vis "Bekreft lagring" KUN etter at bruker har trykket "Lagre" når hasZeroPrice=true */}
            {pendingConfirm && (
              <Stack
                direction='row'
                spacing={0}
                sx={{
                  display: 'flex',
                  width: '100%',
                  flexDirection: 'row-reverse',
                }}
              >
                <Button
                  variant='contained'
                  color='success'
                  sx={{ mr: 1, width: '40%' }}
                  onClick={() => {
                    onSubmit();
                    setPendingConfirm(false);
                  }}
                >
                  {t('Orders.OrderPosts.confirmSave')}
                </Button>
                {/* Valgfritt: avbryt bekreftelse */}
                <Button
                  variant='contained'
                  color='warning'
                  sx={{ mr: 1, width: '40%' }}
                  onClick={() => setPendingConfirm(false)}
                >
                  {t('button.cancel') /* legg til i i18n hvis ikke finnes */}
                </Button>
              </Stack>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
